'use strict';

const {
    setTransformStyle,
    setTransitionStyle,
    getTotalPages,
    getPagedItemWidth,
    pageChangeTypeEnum,
    setWidthStyle
} = require('../utils');
const {noop} = require('../../../mixins/commonjs/basicUtils');
const BaseModel = require('../base/BaseModel');
const root = require('bunsen/helpers/windowCheck');

const DEFAULT_TRANSITION_DURATION = 250;
const TRANSITION_EFFECT_FADE = 'fade';
const TRANSITION_EFFECT_SLIDE = 'slide';

function normalizePage(page, totalPages) {
    // If the requested page is lower than 0, just go to the last page.
    if (page < 0) {
        page = totalPages - 1;
    }

    // If the requested page is higher than the total number of pages, just go to the first page.
    if (page >= totalPages) {
        page = 0;
    }

    return page;
}

/**
 * View-model for carousels that are paged through.
 * @class
 */
class PageModel extends BaseModel {
    constructor({
        itemsPerPage,
        itemCount,
        transitionEffect = TRANSITION_EFFECT_FADE,
        transitionDuration = DEFAULT_TRANSITION_DURATION,
        startingPage = 0,
        onPageChange = noop,
        onStateChange,
        customTransformItemStyle = noop
    } = {}) {
        super({onStateChange});

        if ([TRANSITION_EFFECT_FADE, TRANSITION_EFFECT_SLIDE].indexOf(transitionEffect) === -1) {
            throw new Error(`Transition type must be "${TRANSITION_EFFECT_FADE}" or "${TRANSITION_EFFECT_SLIDE}"`);
        }

        const totalPages = getTotalPages(itemCount, itemsPerPage);

        Object.assign(this, {
            _itemsPerPage: itemsPerPage,
            _itemCount: itemCount,
            _totalPages: totalPages,
            _transitionEffect: transitionEffect,
            _transitionDuration: transitionDuration,
            _onPageChange: onPageChange,
            _handleContainerChange: this._handleContainerChange.bind(this),
            customTransformItemStyle: customTransformItemStyle.bind(this),
            // Calculate the width of each item.
            _width: getPagedItemWidth(itemsPerPage),
            _state: {
                page: normalizePage(startingPage, totalPages),
                containerWidth: 0
            }
        });
    }

    /**
     * Set the page and handle and bounds issues by going to either the first or last page.
     * @param {Number} page The page to go to.
     * @param {PageChangeType} type The type of the page change.
     */
    _setPage(page, type) {
        page = normalizePage(page, this._totalPages);

        // Only update if the page has actually changed.
        if (page !== this._state.page) {
            // TODO: We might want to add a `setState` method on the `BaseModel` like in React that handles calling the
            // `updateState` method so we don't have to remember to do it.
            const previous = this._state.page;
            this._state.page = page;
            this._onPageChange(page, type, previous);
            this._updateState();
        }
    }

    /**
     * Implement the `_afterMount` method from the `BaseModel`.
     */
    _afterMount() {
        // Since the position of the items depends on the carousel container width, we want to update them
        // when the window size changes.
        root.addEventListener('resize', this._handleContainerChange);
        // Update carousel items after the component mounts.
        this._handleContainerChange();
    }

    /**
     * Implement the `destroy` method from the `BaseModel`.
     */
    destroy() {
        root.removeEventListener('resize', this._handleContainerChange);
    }

    /**
     * Go to the specified page.
     */
    jumpToPage(page) {
        this._setPage(page, pageChangeTypeEnum.jump());
    }

    /**
     * Go to the next page.
     */
    nextPage() {
        this._setPage(this._state.page + 1, pageChangeTypeEnum.next());
    }

    /**
     * Go to the previous page
     */
    previousPage() {
        this._setPage(this._state.page - 1, pageChangeTypeEnum.previous());
    }

    calculateStyleValues(i) {
        let transition = '';
        let opacity = '';

        const {_itemsPerPage, _state, _width, _transitionDuration, _transitionEffect} = this;
        const {page} = _state;
        const containerWidth = this._getContainerWidth();

        transition = `${_transitionDuration}ms linear`;

        if (_transitionEffect === TRANSITION_EFFECT_FADE) {
            transition = 'opacity ' + transition;

            const startIndex = page * _itemsPerPage;
            const endIndex = startIndex + _itemsPerPage;
            opacity = 0;

            if ((i >= startIndex) && (i < endIndex)) {
                opacity = 1;
            }
        } else if (_transitionEffect === TRANSITION_EFFECT_SLIDE) {
            transition = 'transform ' + transition;
        }

        return {
            widthValue: _width,
            transformValue: this._getTransformValue(containerWidth),
            transition,
            opacity
        };
    }

    /**
     * Implement the `transformItemStyle` method from the `BaseModel`.
     * @param  {Object} style
     */
    transformItemStyle(style, i) {
        const containerWidth = this._getContainerWidth();

        // Sometimes the container might be mounted but invisible. We can't really know when it becomes visible,
        // so we update the width when there's interaction with the arrows.
        if (!containerWidth && this._el) {
            this._updateContainerWidth();
        }

        const styleResult = Object.assign(this.calculateStyleValues(i), this.customTransformItemStyle(i));

        setTransformStyle(style, `translateX(${ styleResult.transformValue }px)`);
        setWidthStyle(style, styleResult.widthValue);
        setTransitionStyle(style, styleResult.transition);
        style.opacity = styleResult.opacity;
    }

    /**
     * Return the total number of pages.
     * @return {Number}
     */
    getTotalPages() {
        return this._totalPages;
    }

    /**
     * @returns {boolean}
     */
    canPaginate() {
        return this.getTotalPages() > 1;
    }

    /**
     * @return {Number}
     */
    _getContainerWidth() {
        return this._state.containerWidth;
    }

    /**
     * @return {Number}
     */
    _getTransformValue(containerWidth) {
        let transformValue = -containerWidth * this._state.page;

        // Last page
        if (this.getTotalPages() === this._state.page + 1 && this._itemsPerPage !== 1) {
            const itemsInLastPage = this._itemCount - (this._itemsPerPage * this._state.page);

            // Need to adjust transform width if don't have full row at the end
            if (itemsInLastPage < this._itemsPerPage && this._transitionEffect === TRANSITION_EFFECT_SLIDE) {
                transformValue += containerWidth / this._itemsPerPage * (this._itemsPerPage - itemsInLastPage);
            }
        }

        return transformValue;
    }

    /**
     * Updates the container width.
     */
    _updateContainerWidth() {
        const {width: containerWidth} = this._el.getBoundingClientRect();
        this._state.containerWidth = containerWidth;
    }

    /**
     * Handles carousel container width changes.
     */
    _handleContainerChange() {
        this._updateContainerWidth();
        // We want to temporary disable transitions.
        const {_transitionDuration: savedTransitionDuration} = this;
        this._transitionDuration = '0';
        // Trigger to re-render the carousel.
        this._updateState();
        // Re-enable transitions.
        this._transitionDuration = savedTransitionDuration;
    }
}

module.exports = PageModel;



// WEBPACK FOOTER //
// ./assets/js/global/components/carousel/models/PageModel.es.js